/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Sep 19 2023
 **/

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { Column, Divider, Typography } from "@/frameworks/designStuff/components";
import { useFileManager } from "@/frameworks/core/hooks";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";

import * as S from "./private/emotion";
import { Folder, MyFolders } from "./private";
import { useFileBoxContext } from "../../hooks";

export default function Folders() {
	const { getAllFolders, setCurrentFolder } = useFileManager();
	const fileBoxContext = useFileBoxContext();

	const { data, isSuccess } = useQuery({
		queryKey: ["all_folders"],
		queryFn: async () => {
			return await getAllFolders();
		},
		refetchOnMount: true,
		refetchOnWindowFocus: false,
		refetchInterval: 1000 * 60 * 5,
		cacheTime: 0
	});

	useEffect(() => {
		if (!isSuccess) return;
		setCurrentFolder({
			id: data.myRoomId ?? "",
			name: _T("내 파일"),
			isMyFile: true,
			referenceType: "INDIVIDUAL",
			referenceID: data.myRoomId ?? "",
			folderType: "INDIVIDUAL"
		});
	}, [isSuccess]);

	const dmChat = useMemo(() => {
		return data?.chatRooms?.filter((v) => Boolean(v.dm));
	}, [data]);

	return (
		<S.FileBoxListWrapper>
			<Typography typoColor="neutral_90_100" typoSize="Head6" css={{ marginBottom: "24px" }}>
				{fileBoxContext ? _T("파일함 불러오기") : _T("파일함")}
			</Typography>

			<S.FolderWrapper gap={10}>
				<MyFolders />

				<Column gap={10} css={{ width: "100%", height: "100%" }}>
					<Folder type="All" fileBoxType="All" />

					<Column.ul gap={4} css={{ width: "100%" }}>
						<Folder type="All" fileBoxType="Space" />
						{data?.spaces?.map((space, index) => {
							return <Folder key={index} type="Space" {...space} />;
						})}
					</Column.ul>

					<Divider type="line" />

					<Maybe test={dmChat && dmChat?.length > 0}>
						<Folder type="All" fileBoxType="DM" />
						<Column.ul gap={4} css={{ width: "100%" }}>
							{dmChat?.map((chatRoom) => {
								return <Folder key={chatRoom.dm?.roomId} type="DM" {...chatRoom.dm!} />;
							})}
						</Column.ul>
					</Maybe>
				</Column>
			</S.FolderWrapper>
		</S.FileBoxListWrapper>
	);
}
